import React, { useState, forwardRef } from 'react';

import { cn } from '@/lib/tailwind-utils';

export type ToggleProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick' | 'role'> & {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ className, checked: checkedProp, defaultChecked = false, onCheckedChange, disabled, ...props }, ref) => {
    const [uncontrolled, setUncontrolled] = useState(defaultChecked);
    const isControlled = checkedProp !== undefined;
    const checked = isControlled ? checkedProp : uncontrolled;

    const handleClick = () => {
      if (disabled) return;
      const next = !checked;
      if (!isControlled) {
        setUncontrolled(next);
      }
      onCheckedChange?.(next);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          'inline-flex h-6 min-w-[3rem] shrink-0 items-center justify-between rounded-full px-0.5 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50 mx-2',
          checked ? 'bg-black' : 'bg-neutral-400',
          className,
        )}
        {...props}
      >
        {checked ? (
          <>
            <span className="select-none pl-1.5 text-xs font-normal lowercase leading-none text-white">on</span>
            <span aria-hidden className="pointer-events-none size-4 shrink-0 rounded-full bg-white" />
          </>
        ) : (
          <>
            <span aria-hidden className="pointer-events-none size-4 shrink-0 rounded-full bg-white" />
            <span className="select-none pr-1.5 text-xs font-normal lowercase leading-none text-white">off</span>
          </>
        )}
      </button>
    );
  },
);
Toggle.displayName = 'Toggle';

export { Toggle };
